import React, { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { checkAdminKey } from "@/api";
import { BRAND } from "@/config";
import { toast } from "sonner";
import {
  Lock, LogOut, LayoutDashboard, ClipboardList, CalendarDays,
  Map, Users, Wallet, UserCog, Settings,
} from "lucide-react";

const KEY_STORAGE = "hc_admin_key";

const MENU = [
  { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard, testid: "admin-nav-dashboard" },
  { to: "/admin/reservas", label: "Reservas", icon: ClipboardList, testid: "admin-nav-reservas" },
  { to: "/admin/calendario", label: "Calendario", icon: CalendarDays, testid: "admin-nav-calendario" },
  { to: "/admin/tours", label: "Tours", icon: Map, testid: "admin-nav-tours" },
  { to: "/admin/clientes", label: "Clientes", icon: Users, testid: "admin-nav-clientes" },
  { to: "/admin/pagos", label: "Pagos", icon: Wallet, testid: "admin-nav-pagos" },
  { to: "/admin/equipo", label: "Equipo", icon: UserCog, testid: "admin-nav-equipo" },
  { to: "/admin/configuracion", label: "Configuración", icon: Settings, testid: "admin-nav-configuracion" },
];

export default function AdminLayout() {
  const [authorized, setAuthorized] = useState(false);
  const [checking, setChecking] = useState(true);
  const [input, setInput] = useState("");

  useEffect(() => {
    const key = sessionStorage.getItem(KEY_STORAGE);
    if (!key) {
      setChecking(false);
      return;
    }
    checkAdminKey(key)
      .then(() => setAuthorized(true))
      .catch(() => sessionStorage.removeItem(KEY_STORAGE))
      .finally(() => setChecking(false));
  }, []);

  const unlock = async (e) => {
    e.preventDefault();
    try {
      await checkAdminKey(input);
      sessionStorage.setItem(KEY_STORAGE, input);
      setAuthorized(true);
    } catch {
      toast.error("Clave de acceso incorrecta");
    }
  };

  const logout = () => {
    sessionStorage.removeItem(KEY_STORAGE);
    setAuthorized(false);
    setInput("");
  };

  if (checking) {
    return <div className="min-h-screen bg-[#050908] flex items-center justify-center text-[#A3A095]">Cargando…</div>;
  }

  if (!authorized) {
    return (
      <div data-testid="admin-login" className="min-h-screen bg-[#050908] flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <Lock size={32} className="mx-auto text-[#C4A661]" strokeWidth={1.2} />
          <h1 className="font-serif-hc text-4xl text-center mt-6 text-[#F5F2EA]">Centro de operaciones</h1>
          <p className="text-center text-[#A3A095] text-sm mt-3">Acceso exclusivo para el equipo Huitzil-Coatl.</p>
          <form onSubmit={unlock} className="mt-8 space-y-4">
            <input
              data-testid="admin-key-input"
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Clave de acceso"
              autoComplete="current-password"
              className="w-full bg-transparent border border-[#1E2B25] focus:border-[#C4A661] outline-none p-4 text-[#F5F2EA]"
            />
            <button data-testid="admin-key-submit" type="submit" className="hc-btn-primary w-full">
              Entrar
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-[#050908] md:flex">
      {/* Sidebar */}
      <aside className="md:w-60 md:min-h-screen shrink-0 border-b md:border-b-0 md:border-r border-[#1E2B25] bg-[#080D0B] md:sticky md:top-0 md:h-screen flex md:flex-col">
        <div className="hidden md:flex items-center gap-3 px-6 py-6 border-b border-[#1E2B25]">
          <img src={BRAND.logoUrl} alt="Huitzil-Coatl" className="h-9 w-auto" />
          <div>
            <p className="text-[#F5F2EA] text-sm leading-tight">Huitzil-Coatl</p>
            <p className="text-[10px] tracking-[0.2em] uppercase text-[#C4A661]">Operaciones</p>
          </div>
        </div>
        <nav className="flex md:flex-col flex-1 overflow-x-auto scrollbar-hide md:overflow-visible px-3 md:px-3 py-2 md:py-4 gap-1">
          {MENU.map(({ to, end, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 text-[12px] tracking-[0.12em] uppercase whitespace-nowrap border-l-2 transition-colors ${
                  isActive
                    ? "text-[#C4A661] border-[#C4A661] bg-[#C4A661]/5"
                    : "text-[#A3A095] border-transparent hover:text-[#F5F2EA]"
                }`
              }
            >
              <Icon size={15} strokeWidth={1.6} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden md:block px-6 py-5 border-t border-[#1E2B25]">
          <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-[#A3A095] hover:text-[#F5F2EA] transition-colors">
            <LogOut size={14} /> Salir
          </button>
        </div>
        <button data-testid="admin-logout-mobile" onClick={logout} className="md:hidden px-4 text-[#A3A095]" aria-label="Salir">
          <LogOut size={16} />
        </button>
      </aside>

      {/* Content */}
      <main className="flex-1 min-w-0 px-5 md:px-10 py-8 md:py-10">
        <Outlet />
      </main>
    </div>
  );
}
